import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { SectionHead } from '../SectionHead';
import { ObjectChip } from '../ObjectChip';
import type { FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto } from '../../../api/generated/models';

interface Props {
  dueTasks: FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto[];
}

export function DueTasksCard({ dueTasks }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead
        kicker={t('admin.dueTasks.kicker')}
        title={t('admin.dueTasks.title')}
        action={
          <Link
            to="/planning"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-700 dark:text-primary-300"
          >
            {t('admin.dueTasks.schedule')}
            <ArrowRight className="w-3.5 h-3.5" aria-hidden />
          </Link>
        }
      />
      {dueTasks.length === 0 ? (
        <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/10 py-6 text-sm text-emerald-700 dark:text-emerald-200">
          <CheckCircle2 className="w-5 h-5" aria-hidden />
          {t('admin.dueTasks.empty')}
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-border-subtle dark:divide-slate-800">
          {dueTasks.slice(0, 6).map((task) => (
            <li key={task.taskId} className="py-2.5 flex items-center gap-3 min-w-0">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink dark:text-slate-50 truncate">
                  {task.name ?? '—'}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-muted dark:text-slate-400">
                  <ObjectChip
                    objectKey={task.objectId ?? task.objectName}
                    label={task.objectName ?? '—'}
                  />
                  {!!task.estimatedDurationMinutes && (
                    <span className="inline-flex items-center gap-1 font-mono-num">
                      <Clock className="w-3 h-3" aria-hidden />
                      {t('admin.dueTasks.duration', { min: task.estimatedDurationMinutes })}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
